import React, { useContext, useEffect, useMemo, useState } from 'react';

import Inner from '@/components/Inner';
import RecommendationCard from '@/components/RecommandationCard';
import { AddProductPopin } from '@/components/AddProductPopin/AddProductPopin';
import { ProcessContext } from '@/screens/Auth/ProcessAI';
import { TCustomProduct } from '@/helpers/API/requests/interfaces/customer';
import Styles from './styles.module.scss';

import { TSubRoutine } from './interfaces';

const SubRoutine: React.FC<TSubRoutine> = ({
  index,
  rIndex,
  parentIndex,
  routine,
  isCurrentStep,
  active,
  selectedProducts,
  isSvrLab,
  onCardSelect,
}) => {
  const { addCustomProduct, customProducts } = useContext(ProcessContext);

  const routineCustomProducts = useMemo(
    () =>
      customProducts.filter(
        (p) =>
          p.routinePosition === rIndex &&
          p.parentRoutinePosition === parentIndex,
      ),
    [customProducts, rIndex, parentIndex],
  );

  const [activeCards, setActiveCards] = useState<number[]>(
    [...routine.proposed_products, ...routineCustomProducts].reduce(
      (acc, product, i) => {
        if (active && product.id === active.id) {
          acc.push(i);
        }
        return acc;
      },
      [] as number[],
    ),
  );
  const [isAddProductPopinOpen, setIsAddProductPopinOpen] = useState(false);

  const onProductSelect = (
    id: number,
    name: string,
    isInitial: boolean,
    active: boolean,
    routineIndex: number,
    pIndex: number,
    image_url?: string,
  ) => {
    setActiveCards((prevActiveCards) => {
      if (active) {
        return [...prevActiveCards, pIndex];
      } else {
        return prevActiveCards.filter((i) => i !== pIndex);
      }
    });

    onCardSelect(id, active, routineIndex);
  };

  const handleAddProduct = (name: string, brand: string) => {
    const newProduct: TCustomProduct = {
      id: Date.now(),
      name,
      brand: { id: 0, name: brand },
      description: '',
      is_bought: false,
      image_url: '',
      price: null,
      reducedPrice: null,
      routinePosition: rIndex,
      parentRoutinePosition: parentIndex,
    };

    addCustomProduct(newProduct);
    setIsAddProductPopinOpen(false);
  };

  useEffect(function syncActiveCards() {
    const allProducts = [...routine.proposed_products, ...routineCustomProducts];
    const activeIndices = allProducts.reduce((acc, product, i) => {
      const isActive =
        (active && product.id === active.id) ||
        selectedProducts?.some((p) => p?.id === product.id);
      if (isActive) acc.push(i);
      return acc;
    }, [] as number[]);
    setActiveCards(activeIndices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, routine.proposed_products]);

  return (
    <div
      key={'recommendationRoutine-' + parentIndex + '-sub-' + rIndex}
      className={`${Styles['recommendationRoutine']} ${isCurrentStep ? Styles['recommendationRoutine--active'] : ''}`}
    >
      <Inner className={Styles['recommendationRoutine__heading']}>
        <div className={Styles['recommendationRoutine__title']}>
          <span>
            {parentIndex + 1}.{rIndex + 1}
          </span>
          {routine.title}
        </div>
      </Inner>
      {isSvrLab && (
        <Inner type='full'>
          <button
            onClick={() => setIsAddProductPopinOpen(true)}
            className={Styles.recommendationRoutine__addButton}
          >
            + Ajouter un produit
          </button>
        </Inner>
      )}
      <div className={Styles['recommendationRoutine__body']}>
        <Inner type='full'>
          <div className={Styles['recommendationRoutine__slider']}>
            {[...routine.proposed_products, ...routineCustomProducts].map((product: any, pIndex: number) => (
              <RecommendationCard
                key={'recommendationCard-' + pIndex}
                {...product}
                isVisible={isCurrentStep}
                isActive={activeCards.includes(pIndex)}
                onSelect={(id, name, isInitial, active, i, image_url) =>
                  onProductSelect(id, name, isInitial, active, index, pIndex, image_url)
                }
              />
            ))}
          </div>
        </Inner>
      </div>
      {isSvrLab && (
        <AddProductPopin
          active={isAddProductPopinOpen}
          title='Ajouter un produit'
          onClose={() => setIsAddProductPopinOpen(false)}
          onSubmit={handleAddProduct}
        />
      )}
    </div>
  );
};

export default SubRoutine;
